import { useState, type FormEvent } from 'react';
import type { CreateUserRequest, UpdateUserRequest, UserRole, UserSummaryDto } from '@/api/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import styles from './Users.module.css';

interface UserDialogProps {
  user: UserSummaryDto | null;
  currentUserId: string;
  busy: boolean;
  onClose: () => void;
  onCreate: (request: CreateUserRequest) => Promise<void>;
  onUpdate: (id: string, request: UpdateUserRequest) => Promise<void>;
  onResetPassword: (user: UserSummaryDto) => void;
}

export function UserDialog({
  user,
  currentUserId,
  busy,
  onClose,
  onCreate,
  onUpdate,
  onResetPassword,
}: UserDialogProps) {
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<UserRole>(user?.role ?? 'User');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const editingSelf = user?.id === currentUserId;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!displayName.trim() || !email.trim()) {
      setError('Display name and email are required.');
      return;
    }
    if (!user && temporaryPassword.length < 12) {
      setError('The temporary password must be at least 12 characters.');
      return;
    }

    try {
      if (user) {
        await onUpdate(user.id, {
          displayName: displayName.trim(),
          email: email.trim(),
          role,
          isActive,
        });
      } else {
        await onCreate({
          displayName: displayName.trim(),
          email: email.trim(),
          role,
          temporaryPassword,
        });
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to save the user.');
    }
  }

  return (
    <Dialog
      title={user ? 'Edit user' : 'Add user'}
      onClose={onClose}
      actions={(
        <>
          {user && (
            <Button
              type="button"
              variant="outline-warning"
              disabled={busy}
              onClick={() => onResetPassword(user)}
            >
              Reset password
            </Button>
          )}
          <span className={styles.dialogSpacer} />
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" form="user-form" disabled={busy}>
            {busy ? 'Saving...' : user ? 'Save' : 'Create'}
          </Button>
        </>
      )}
    >
      <form id="user-form" className={styles.form} onSubmit={handleSubmit}>
        {error && <Alert variant="danger">{error}</Alert>}
        <Input
          label="Display name"
          required
          maxLength={128}
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
        <Input
          label="Email"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Select
          label="Role"
          value={role}
          disabled={editingSelf}
          onChange={(event) => setRole(event.target.value as UserRole)}
          options={[
            { value: 'User', label: 'User' },
            { value: 'Admin', label: 'Admin' },
          ]}
        />
        {!user && (
          <>
            <Input
              label="Temporary password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={temporaryPassword}
              onChange={(event) => setTemporaryPassword(event.target.value)}
            />
            <p className={styles.hint}>The user must change it on first login.</p>
          </>
        )}
        {user && (
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={isActive}
              disabled={editingSelf}
              onChange={(event) => setIsActive(event.target.checked)}
            />
            Active account
          </label>
        )}
      </form>
    </Dialog>
  );
}
